import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Pressable, Text, TextInput, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDoc } from '../../store/store';
import { addQuick, toggleQuick, removeQuick, duplicarQuick, addBox, archiveBox, unarchiveBox, ordenarQuick, ordenarCaixas, caixaRapida, duplicarBox, moveBox, ID_RAPIDA } from '../../store/anotacoes';
import { Ordenavel } from '../../components/Arrastar';
import { BoxCard, SwipeBox, confirmarExclusao, excluirCaixa, limparTodasAsCaixas } from '../../components/BoxCard';
import { AcoesItem } from '../../components/Acoes';
import { Arquivados, useSelecao } from '../../components/Selecao';
import { useOrganizar } from '../../lib/organizar';
import { paraLixeira } from '../../lib/lixeira';
import { useBotaoNovo } from '../../lib/botaoNovo';
import { useTaskSheet } from '../../components/TaskSheet';
import { ActionSheet, Screen, type Action } from '../../components/ui';
import { voltarAoMyPlanner } from '../../lib/voltarPlanner';
import { TravaAba } from '../../components/TravaAba';
import type { Box, Item } from '../../store/types';
import { ABAS, C, useCores } from '../../components/theme';
import { uid } from '../../lib/date';
import { t } from '../../lib/i18n';
import { useCaixas } from '../../lib/caixas';
export { ErrorBoundary } from '../../components/Falha';

// texto 10 (D): "Pedir senha ao abrir esta aba" (Ajustes > Abas > Anotações)
export default function AnotacoesTela() {
  return <TravaAba id="anotacoes" onBack={voltarAoMyPlanner}><Anotacoes /></TravaAba>;
}

function Anotacoes() {
  const { item } = useLocalSearchParams<{ item?: string }>();
  const { quick, boxes } = useDoc('anotacoes');
  const { corDe } = useCores();
  const cor = corDe('anotacoes');
  const m = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const sheet = useTaskSheet();
  const [txt, setTxt] = useState('');
  const input = useRef<TextInput>(null);
  const org = useOrganizar('anotacoes');            // ⭐ fixar caixas (o arquivo das caixas é o próprio: archivedAt)
  const orgQ = useOrganizar('anotacoes-rapida');    // 📦 e ⭐ dos itens da lista rápida
  const minhas = org.separar(boxes.filter(b => b.aba === 'anotacoes' && !b.archivedAt && b.id !== ID_RAPIDA), b => b.id).ativos;
  const rapidos = orgQ.separar(quick as Item[], q => q.id);
  // 11d F: o + cria DIRETO uma nota rápida (antes criava uma caixa lá no fim da lista, fora da vista: parecia não fazer nada).
  // A janelinha fica no alto da tela (o teclado não cobre); Enter guarda na Lista rápida e já deixa escrever a próxima.
  const ins = useSafeAreaInsets();
  const [nota, setNota] = useState(false);
  const txtNota = useRef('');
  const [txtNotaV, setTxtNotaV] = useState('');
  const mudarNota = (v: string) => { txtNota.current = v; setTxtNotaV(v); };
  const guardarNota = () => { if (txtNota.current.trim()) addQuick(txtNota.current); mudarNota(''); };
  const fecharNota = () => { guardarNota(); setNota(false); };
  useBotaoNovo({ rotulo: t('Nova nota rápida'), fn: () => setNota(true) });
  // excluir sempre passa pela lixeira (30 dias)
  const excluirRapido = (q: Item) => { paraLixeira('anotacoes', 'rapida', q.text, q); removeQuick(q.id); };
  const confirmarRapido = (q: Item) => Alert.alert(t('Excluir {nome}?', { nome: q.text || t('este item') }), t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirRapido(q) }]);
  const feitos = rapidos.ativos.filter(q => q.done);
  const limparFeitos = () => Alert.alert(t('Excluir {n} item(ns)?', { n: feitos.length }), t('Ficam 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => feitos.forEach(excluirRapido) }]);
  // texto 29 (4b): os itens vazios que já existiam somem na primeira abertura
  useEffect(() => { limparTodasAsCaixas(); }, []);
  const selQ = useSelecao({ itens: rapidos.ativos, chave: q => q.id, cor, onArquivar: ids => orgQ.arquivar(ids), onDuplicar: ids => ids.forEach(duplicarQuick),
    onExcluir: ids => ids.forEach(id => { const q = quick.find(x => x.id === id); if (q) excluirRapido(q); }) });
  // texto 29 (item 2, REGRAS 1.4): caixas selecionadas: Arquivar, Duplicar, Enviar e Excluir
  const { liberada } = useCores();
  const [enviarVarias, setEnviarVarias] = useState<Action[] | null>(null);
  const selB = useSelecao({ itens: minhas, chave: b => b.id, cor, onArquivar: ids => ids.forEach(archiveBox), onDuplicar: ids => ids.forEach(duplicarBox),
    extras: [{ label: '↗ ' + t('Enviar'), onPress: ids => setTimeout(() => setEnviarVarias(ABAS.filter(x => ['casa', 'bemestar', 'financas'].includes(x.id)).map(x => ({
      label: `${x.icon} ${x.nome}${liberada(x.id) ? '' : ' 🔒'}`, onPress: () => {
        if (!liberada(x.id)) return Alert.alert(t('{aba} está bloqueada', { aba: x.nome }), t('Assine a aba em Ajustes › Compras para enviar anotações para ela.'));
        ids.forEach(id => moveBox(id, x.id));
        Alert.alert(ids.length === 1 ? t('Enviada') : t('Enviadas'), ids.length === 1 ? t('A caixa está em {aba}, em "Anotações recebidas".', { aba: x.nome })
          : t('As {n} caixas estão em {aba}, em "Anotações recebidas".', { n: ids.length, aba: x.nome }));
      } }))), 50) }],
    onExcluir: ids => ids.forEach(id => { const b = boxes.find(x => x.id === id); if (b) excluirCaixa(b); }) });
  const rapida = boxes.find(b => b.id === ID_RAPIDA);
  useEffect(() => { if (!rapida) caixaRapida(); }, [rapida]);
  const arquivadas = boxes.filter(b => !!b.archivedAt && b.id !== ID_RAPIDA && !b.ref).sort((a, b) => (b.archivedAt ?? '').localeCompare(a.archivedAt ?? ''));
  // 0.67: o "Desfazer" do arquivar é o mesmo do app todo (faixa embaixo, 5 segundos)
  const onArquivada = useCallback((_b: Box) => {}, []);

  return (
    <View style={{ flex: 1 }}>
    <Screen title={t('Anotações Rápidas')} onGesto={voltarAoMyPlanner} ajuda="anotacoes">

      <View style={{ backgroundColor: C.card, borderRadius: m.raio(18), ...m.pads(14), gap: m.gap(4) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 17, flex: 1 }}>⚡ {t('Lista rápida')}</Text>
          {feitos.length > 0 && <Pressable onPress={limparFeitos}><Text style={{ color: C.sub, fontSize: 12 }}>{t('Limpar feitos')}</Text></Pressable>}
        </View>
        {selQ.barra}
        <Ordenavel itens={rapidos.ativos} chave={q => q.id} gap={0} onOrdem={l => ordenarQuick([...l, ...rapidos.arquivados])} selecao={selQ.prop} render={q => (
          <AcoesItem lembrar={{ aba: 'anotacoes', id: q.id, titulo: q.text }} nome={q.text} semConfirmar onExcluir={() => confirmarRapido(q)} onArquivar={() => orgQ.arquivar(q.id)} onDuplicar={() => duplicarQuick(q.id)}
            fixar={{ on: orgQ.fixado(q.id), alternar: () => orgQ.alternarFixo(q.id) }}
            pacote={() => ({ titulo: q.text, origem: 'anotacoes', remover: () => removeQuick(q.id) })}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: m.pad(6), paddingRight: 26, backgroundColor: C.card }}>
            <Pressable hitSlop={8} onPress={() => toggleQuick(q.id)} style={{ width: 20, height: 20, borderRadius: 6, borderWidth: 2,
              borderColor: q.done ? cor : C.mute, backgroundColor: q.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
              {q.done && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}
            </Pressable>
            <Text style={{ color: C.text, flex: 1, textDecorationLine: q.done ? 'line-through' : 'none', opacity: q.done ? 0.55 : 1 }}>{q.text}</Text>
            <Pressable hitSlop={8} onPress={() => sheet.open({ prefill: { title: q.text }, onSaved: () => removeQuick(q.id) })}><Text>📅</Text></Pressable>
            <Pressable hitSlop={8} onPress={() => confirmarRapido(q)}><Text style={{ color: C.mute }}>✕</Text></Pressable>
          </View>
          </AcoesItem>
        )} />
        <Arquivados itens={rapidos.arquivados} chave={q => q.id} cor={cor} render={q => <Text style={{ color: C.text }}>{q.text}</Text>}
          onRestaurar={q => orgQ.restaurar(q.id)} onExcluir={confirmarRapido} />
        <TextInput ref={input} value={txt} onChangeText={setTxt} placeholder={t('Escreva e dê Enter (ex.: lavar a máquina)')} placeholderTextColor={C.mute}
          blurOnSubmit={false} returnKeyType="done" style={{ color: C.text, paddingVertical: m.pad(10) }}
          onSubmitEditing={() => { if (txt.trim()) { addQuick(txt); setTxt(''); } }} />
        {/* checklist, lista, texto livre e desenho, como nas outras caixas */}
        {rapida && <BoxCard box={rapida} embutida />}
        {quick.length > 0 && (
          <Pressable onPress={() => sheet.open({ prefill: { title: t('Lista rápida'), subtasks: quick.filter(q => !q.done).map(q => ({ id: uid(), text: q.text })) } })}>
            <Text style={{ color: cor, fontSize: 12 }}>{t('Copiar a lista para uma tarefa')} ↗</Text>
          </Pressable>
        )}
      </View>

      {/* texto 28 (B6): a frase de ajuda saiu daqui; o passo a passo fica no ⓘ do topo e em "Como usar esta aba", no fim */}
      {selB.barra}
      <Ordenavel itens={minhas} chave={b => b.id} alca gap={m.gap(10)} onOrdem={ordenarCaixas} selecao={selB.prop}
        render={(b, _i, a) => <SwipeBox box={b} onArquivada={onArquivada}><BoxCard box={b} destaque={b.id === item} alca={a.alca}
          fixar={{ on: org.fixado(b.id), alternar: () => org.alternarFixo(b.id) }} onArquivar={() => { archiveBox(b.id); onArquivada(b); }}
          onSelecionar={() => selB.selecionar(b.id)} /></SwipeBox>} />
      <ActionSheet title={t('Enviar para')} actions={enviarVarias} onClose={() => setEnviarVarias(null)} />

      <Pressable onPress={() => addBox()} style={{ borderWidth: 1, borderStyle: 'dashed', borderColor: C.line, borderRadius: m.raio(18), padding: m.pad(18), alignItems: 'center' }}>
        <Text style={{ color: C.sub }}>+ {t('Nova caixa (ex.: Compras, Ideias)')}</Text>
      </Pressable>
      {/* 0.65: caixas arquivadas no padrão único do app — linha recolhida no fim da lista, abre aqui mesmo */}
      <Arquivados itens={arquivadas} chave={b => b.id} cor={cor} render={b => <ResumoArquivada box={b} />}
        onRestaurar={b => unarchiveBox(b.id)} onExcluir={b => confirmarExclusao(b)} />
      <View style={{ height: 90 }} />
    </Screen>
    {nota && (
      <View style={{ position: 'absolute', top: ins.top + 8, left: 12, right: 12, backgroundColor: C.card, borderRadius: 16, padding: 12, gap: 4, borderWidth: 1, borderColor: cor,
        shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 10, shadowOffset: { width: 0, height: 4 }, elevation: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>⚡ {t('Lista rápida')}</Text>
          <Pressable hitSlop={10} onPress={fecharNota} accessibilityLabel={t('Fechar')}><Text style={{ color: C.mute, fontSize: 16 }}>✕</Text></Pressable>
        </View>
        <TextInput autoFocus value={txtNotaV} onChangeText={mudarNota} placeholder={t('Escreva e dê Enter (ex.: lavar a máquina)')} placeholderTextColor={C.mute}
          blurOnSubmit={false} returnKeyType="done" onSubmitEditing={guardarNota} onBlur={fecharNota} style={{ color: C.text, paddingVertical: 8 }} />
      </View>
    )}
    </View>
  );
}

/** Caixa arquivada na lista de arquivados: título e o começo do que tem dentro (só leitura). */
function ResumoArquivada({ box }: { box: Box }) {
  const itens = box.blocks.flatMap(bl => (bl.type === 'text' ? (bl.text.trim() ? [bl.text.trim()] : []) : bl.items.map(i => i.text.trim()).filter(Boolean)));
  return (
    <View style={{ gap: 2 }}>
      <Text style={{ color: C.text, fontWeight: '700' }} numberOfLines={1}>{box.title || t('Sem título')}</Text>
      {!!itens.length && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={2}>{itens.join(' · ')}</Text>}
    </View>
  );
}
